import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { getCurrentUserProfile } from '../lib/api/profile';
import { sendContactMessage } from '../lib/api/email';

interface ServiceContactModalProps {
  open: boolean;
  onClose: () => void;
  serviceId: string;
  serviceTitle: string;
  providerInfo: any;
}

const ServiceContactModal: React.FC<ServiceContactModalProps> = ({
  open,
  onClose,
  serviceId,
  serviceTitle,
  providerInfo
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [_, setUserProfile] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientCompany: ''
  });

  // Load user profile when modal opens
  React.useEffect(() => {
    if (open) {
      loadUserProfile();
    }
  }, [open]);

  const loadUserProfile = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const profile = await getCurrentUserProfile();
      if (profile) {
        setUserProfile(profile);
        setFormData(prev => ({
          ...prev,
          clientName: profile.full_name || '',
          clientEmail: profile.email || '',
          clientPhone: profile.phone || '',
          clientCompany: profile.company || ''
        }));
      }
    } catch (err: any) {
      setError(err.message || 'Грешка при зареждане на профила');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.subject.trim() || !formData.message.trim()) {
      setError('Моля, попълнете всички задължителни полета');
      return;
    }

    if (!providerInfo?.provider?.email) {
      setError('Не е намерен имейл адрес на изпълнителя');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const result = await sendContactMessage({
        clientName: formData.clientName,
        clientEmail: formData.clientEmail,
        clientPhone: formData.clientPhone,
        clientCompany: formData.clientCompany,
        subject: formData.subject,
        message: formData.message,
        serviceTitle: serviceTitle,
        serviceId: serviceId,
        executorEmail: providerInfo.provider.email,
        executorName: providerInfo.provider.full_name || providerInfo.provider.company || 'Изпълнител'
      });

      if (result.success) {
        setSuccess(true);
        
        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setFormData({
            subject: '',
            message: '',
            clientName: '',
            clientEmail: '',
            clientPhone: '',
            clientCompany: ''
          });
        }, 2000);
      } else {
        setError(result.error || 'Грешка при изпращане на съобщението');
      }
      
    } catch (err: any) {
      setError(err.message || 'Грешка при изпращане на съобщението');
    } finally {
      setSubmitting(false);
    }
  };

  if (!providerInfo) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a2e',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle sx={{ color: '#ffffff', textAlign: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          <EmailIcon sx={{ color: '#28A0F0', fontSize: '2rem' }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Свържете се с изпълнителя
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ color: '#10b981', fontSize: '4rem', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#10b981', mb: 1 }}>
              Съобщението е изпратено успешно!
            </Typography>
            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
              Вашето съобщение е изпратено до изпълнителя.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* Service Info */}
            <Card sx={{ backgroundColor: 'rgba(40, 160, 240, 0.1)', border: '1px solid rgba(40, 160, 240, 0.3)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
                  {serviceTitle}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                  Ще изпратите съобщение до изпълнителя на тази услуга
                </Typography>
              </CardContent>
            </Card>

            {/* Provider Info */}
            <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                  Съобщение до изпълнителя
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                  Вашето съобщение ще бъде изпратено директно до изпълнителя на услугата.
                </Typography>
              </CardContent>
            </Card>

            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

            {/* Contact Form */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#28A0F0' }} />
              </Box>
            ) : (
              <Stack spacing={3}>
                <Typography variant="h6" sx={{ color: '#ffffff' }}>
                  Ваши данни
                </Typography>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Вашето име"
                    value={formData.clientName}
                    onChange={(e) => handleInputChange('clientName', e.target.value)}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#ffffff',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&:hover fieldset': { borderColor: '#28A0F0' },
                        '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                      },
                      '& .MuiInputLabel-root': { color: '#a0a0a0' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Вашият имейл"
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#ffffff',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&:hover fieldset': { borderColor: '#28A0F0' },
                        '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                      },
                      '& .MuiInputLabel-root': { color: '#a0a0a0' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Телефон (по избор)"
                    value={formData.clientPhone}
                    onChange={(e) => handleInputChange('clientPhone', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#ffffff',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&:hover fieldset': { borderColor: '#28A0F0' },
                        '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                      },
                      '& .MuiInputLabel-root': { color: '#a0a0a0' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Компания (по избор)"
                    value={formData.clientCompany}
                    onChange={(e) => handleInputChange('clientCompany', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#ffffff',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&:hover fieldset': { borderColor: '#28A0F0' },
                        '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                      },
                      '& .MuiInputLabel-root': { color: '#a0a0a0' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                    }}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Тема на съобщението"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: '#28A0F0' },
                      '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                    },
                    '& .MuiInputLabel-root': { color: '#a0a0a0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                  }}
                />

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Съобщение"
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  required
                  placeholder="Опишете вашите изисквания и очаквания..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: '#28A0F0' },
                      '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                    },
                    '& .MuiInputLabel-root': { color: '#a0a0a0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                  }}
                />

                {error && (
                  <Alert severity="error" sx={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                    {error}
                  </Alert>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={onClose}
            sx={{ 
              color: '#a0a0a0',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Отказ
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || submitting || !formData.subject.trim() || !formData.message.trim()}
            variant="contained"
            startIcon={<SendIcon />}
            sx={{
              background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
              color: '#ffffff',
              px: 3,
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#a0a0a0'
              }
            }}
          >
            {submitting ? 'Изпращане...' : 'Изпрати съобщение'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ServiceContactModal;
